import React, { useState } from 'react'
import { formatDateTime } from '../../../../shared/sso-workspace/format'
import { keyIDFromFileName, p8Problem, parseServiceAccount, readText } from './vapid'

export const CREDENTIAL_PROVIDERS = [
  { key: 'apns', label: 'Apple (APNs)', help: 'An APNs auth key (.p8) for iPhone, iPad, Mac and Vision Pro apps.' },
  { key: 'fcm', label: 'Firebase (FCM)', help: 'A Firebase service-account key (.json) for Android apps.' },
  { key: 'webpush', label: 'Web Push', help: 'A VAPID key pair for browsers: made by the server, or an existing pair pasted.' }
]

const TEN = /^[A-Z0-9]{10}$/
const SUBJECT = /^(mailto:\S+@\S+|https:\/\/\S+)$/

// The form's starting values: blank, or the metadata of the credential
// being rotated (never its secret, which the admin never has).
export const initialForm = (credential, provider = 'apns') => {
  const m = (credential && credential.metadata) || {}
  return {
    provider: credential ? credential.provider : provider,
    label: credential ? credential.label || '' : '',
    environment: credential ? credential.environment || 'production' : 'production',
    keyId: m.keyId || '',
    teamId: m.teamId || '',
    topics: (m.topics || []).join(', '),
    subject: m.subject || '',
    publicKey: '',
    keySource: 'generate',
    graceHours: '24'
  }
}

// A Web Push pair notify makes itself (POST /notify/credentials/generate-vapid):
// only the label and subject travel; the private key never leaves the server.
export const generatesOnServer = form => form.provider === 'webpush' && form.keySource === 'generate'

// What the form would send (C-4 CredentialCreate, plus graceHours on a
// rotate), or null while it is incomplete. `secret` comes from the file read
// or the key pair, held only in the component's memory.
export const credentialBody = (form, secret, { rotate = false } = {}) => {
  const grace = () => {
    const g = Number(form.graceHours)
    return Number.isInteger(g) && g >= 0 && g <= 720 ? g : null
  }
  if (!form.label.trim()) return null
  if (generatesOnServer(form)) {
    if (!SUBJECT.test(form.subject.trim())) return null
    const body = { label: form.label.trim(), subject: form.subject.trim() }
    if (rotate) {
      if (grace() === null) return null
      body.graceHours = grace()
    }
    return body
  }
  if (!secret) return null
  const body = { provider: form.provider, environment: form.environment, label: form.label.trim(), secret }
  if (form.provider === 'apns') {
    const keyId = form.keyId.trim().toUpperCase()
    const teamId = form.teamId.trim().toUpperCase()
    const topics = form.topics.split(',').map(t => t.trim()).filter(Boolean)
    if (!TEN.test(keyId) || !TEN.test(teamId) || topics.length === 0) return null
    Object.assign(body, { keyId, teamId, topics })
  } else if (form.provider === 'webpush') {
    if (!SUBJECT.test(form.subject.trim())) return null
    // The public key is optional: notify derives it from the private key
    // (and refuses one that doesn't match).
    if (form.publicKey.trim()) body.publicKey = form.publicKey.trim()
    body.subject = form.subject.trim()
  }
  if (rotate) {
    if (grace() === null) return null
    body.graceHours = grace()
  }
  return body
}

// Add or rotate a push credential (spec section 12, OD-18). The secret --
// the .p8 key, the service-account JSON, the VAPID private key -- is read
// from the file or generated here, kept only in this form's memory, sent
// once as `secret` and never shown: the page says which file is ready, and
// shows only what the list will show (key and team ids, project and
// account, the public key). `credential` is the one being rotated (its
// provider is fixed; its metadata fills the form). onSubmit(body).
export const CredentialForm = ({ credential, busy, error, errorReference, onSubmit, onCancel }) => {
  const rotating = Boolean(credential)
  const [form, setForm] = useState(() => initialForm(credential))
  const [secret, setSecret] = useState('')
  const [fileNote, setFileNote] = useState(null)
  const [fileError, setFileError] = useState(null)
  const [account, setAccount] = useState(null)

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }))

  const pickProvider = provider => {
    setForm(f => ({ ...f, provider }))
    setSecret('')
    setFileNote(null)
    setFileError(null)
    setAccount(null)
  }

  const takeP8 = async file => {
    setSecret('')
    setFileNote(null)
    setFileError(null)
    if (!file) return
    const text = await readText(file)
    const problem = p8Problem(text)
    if (problem) {
      setFileError(problem)
      return
    }
    setSecret(text)
    const id = keyIDFromFileName(file.name)
    if (id) set('keyId', id)
    setFileNote(`${file.name} is ready. It is sent when you ${rotating ? 'rotate' : 'add'}, and never shown again.`)
  }

  const takeServiceAccount = async file => {
    setSecret('')
    setAccount(null)
    setFileNote(null)
    setFileError(null)
    if (!file) return
    const text = await readText(file)
    try {
      setAccount(parseServiceAccount(text))
      setSecret(text)
      setFileNote(`${file.name} is ready. Its private key is sent when you ${rotating ? 'rotate' : 'add'}, and never shown again.`)
    } catch (e) {
      setFileError(e.message)
    }
  }

  const body = credentialBody(form, secret, { rotate: rotating })
  const graceUntil = rotating && Number.isInteger(Number(form.graceHours)) ? new Date(Date.now() + Number(form.graceHours) * 3600 * 1000) : null

  const submit = e => {
    e.preventDefault()
    if (!body || busy) return
    onSubmit(body, { generate: generatesOnServer(form) })
  }

  return (
    <form onSubmit={submit} className="nf-credential-form">
      {!rotating &&
        <fieldset className="mb-3">
          <legend className="sw-label">Provider</legend>
          {CREDENTIAL_PROVIDERS.map(p =>
            <div className="form-check" key={p.key}>
              <input className="form-check-input" type="radio" name="nf-cred-provider" id={`nf-cred-${p.key}`} checked={form.provider === p.key} onChange={() => pickProvider(p.key)} />
              <label className="form-check-label" htmlFor={`nf-cred-${p.key}`}>{p.label}</label>
              <div className="sw-hint small">{p.help}</div>
            </div>)}
        </fieldset>}

      <div className="sw-field mb-2">
        <label className="sw-label" htmlFor="nf-cred-label">Label</label>
        <input id="nf-cred-label" className="form-control" maxLength={120} value={form.label} onChange={e => set('label', e.target.value)} />
      </div>
      {!generatesOnServer(form) && <div className="sw-field mb-3">
        <label className="sw-label" htmlFor="nf-cred-env">Environment</label>
        <select id="nf-cred-env" className="form-select" value={form.environment} onChange={e => set('environment', e.target.value)}>
          <option value="production">Production</option>
          <option value="sandbox">Sandbox (development builds)</option>
        </select>
      </div>}

      {form.provider === 'apns' &&
        <>
          <div className="sw-field mb-2">
            <label className="sw-label" htmlFor="nf-cred-p8">APNs key file (.p8)</label>
            <input id="nf-cred-p8" type="file" className="form-control" accept=".p8" onChange={e => { takeP8(e.target.files && e.target.files[0]); e.target.value = '' }} />
          </div>
          <div className="sw-field mb-2">
            <label className="sw-label" htmlFor="nf-cred-keyid">Key ID</label>
            <input id="nf-cred-keyid" className="form-control sw-mono" maxLength={10} value={form.keyId} onChange={e => set('keyId', e.target.value)} />
          </div>
          <div className="sw-field mb-2">
            <label className="sw-label" htmlFor="nf-cred-team">Team ID</label>
            <input id="nf-cred-team" className="form-control sw-mono" maxLength={10} value={form.teamId} onChange={e => set('teamId', e.target.value)} />
          </div>
          <div className="sw-field mb-3">
            <label className="sw-label" htmlFor="nf-cred-topics">Topics (bundle ids)</label>
            <input id="nf-cred-topics" className="form-control sw-mono" placeholder="com.heizenrader.educationxr" value={form.topics} onChange={e => set('topics', e.target.value)} />
            <span className="sw-hint small">Separate several with commas.</span>
          </div>
        </>}

      {form.provider === 'fcm' &&
        <div className="sw-field mb-3">
          <label className="sw-label" htmlFor="nf-cred-sa">Service-account key file (.json)</label>
          <input id="nf-cred-sa" type="file" className="form-control" accept=".json,application/json" onChange={e => { takeServiceAccount(e.target.files && e.target.files[0]); e.target.value = '' }} />
          {account &&
            <dl className="sw-dl mt-2">
              <dt>Project</dt><dd className="sw-mono">{account.projectId}</dd>
              <dt>Service account</dt><dd className="sw-mono">{account.clientEmail}</dd>
            </dl>}
        </div>}

      {form.provider === 'webpush' &&
        <>
          <fieldset className="mb-2">
            <legend className="sw-label">Key pair</legend>
            <div className="form-check">
              <input className="form-check-input" type="radio" name="nf-cred-keysource" id="nf-cred-generate" checked={form.keySource === 'generate'} onChange={() => { set('keySource', 'generate'); setSecret('') }} />
              <label className="form-check-label" htmlFor="nf-cred-generate">Generate it on the server (recommended)</label>
              <div className="sw-hint small">The private key is made and sealed on the server and never leaves it.</div>
            </div>
            <div className="form-check">
              <input className="form-check-input" type="radio" name="nf-cred-keysource" id="nf-cred-paste" checked={form.keySource === 'paste'} onChange={() => set('keySource', 'paste')} />
              <label className="form-check-label" htmlFor="nf-cred-paste">Paste an existing pair</label>
              <div className="sw-hint small">Only to keep the browsers already signed up with an older pair.</div>
            </div>
          </fieldset>
          {form.keySource === 'paste' &&
            <>
              <div className="sw-field mb-2">
                <label className="sw-label" htmlFor="nf-cred-pub">Public key</label>
                <input id="nf-cred-pub" className="form-control sw-mono" value={form.publicKey} onChange={e => set('publicKey', e.target.value)} />
                <span className="sw-hint small">Optional: it is checked against the private key.</span>
              </div>
              <div className="sw-field mb-2">
                <label className="sw-label" htmlFor="nf-cred-priv">Private key</label>
                <input id="nf-cred-priv" type="password" autoComplete="new-password" className="form-control sw-mono" value={secret} onChange={e => setSecret(e.target.value)} />
                <span className="sw-hint small">The base64url P-256 private key. Sent once, never shown again.</span>
              </div>
            </>}
          <div className="sw-field mb-3">
            <label className="sw-label" htmlFor="nf-cred-subject">Subject</label>
            <input id="nf-cred-subject" className="form-control" placeholder="mailto:ops@educationxr.com" value={form.subject} onChange={e => set('subject', e.target.value)} />
            <span className="sw-hint small">A mailto: address or an https: page push services can reach you at.</span>
          </div>
        </>}

      {rotating &&
        <div className="sw-field mb-3">
          <label className="sw-label" htmlFor="nf-cred-grace">Grace period (hours)</label>
          <input id="nf-cred-grace" type="number" min={0} max={720} step={1} className="form-control" value={form.graceHours} onChange={e => set('graceHours', e.target.value)} />
          <span className="sw-hint small">
            {`The current version ${credential.version} keeps working for ${form.graceHours || 0} hours after the new one is active`}
            {graceUntil && !Number.isNaN(graceUntil.getTime()) ? ` (until about ${formatDateTime(graceUntil.toISOString())})` : ''}.
            {' The new key is tested first; if the test fails, it stays pending and nothing else changes.'}
          </span>
        </div>}

      {fileNote && <p className="text-success small" role="status">{fileNote}</p>}
      {fileError && <p className="text-danger small" role="alert">{fileError}</p>}
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {errorReference}

      <div className="d-flex gap-2">
        <button type="submit" className="btn sw-btn-primary" disabled={!body || busy}>{rotating ? 'Rotate' : 'Add'}</button>
        <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
